import React, { FC, useEffect, useRef, useState } from "react";

// Types
import { FormStateType } from "../../Helpers/formTypes";
import { RootDataType } from "../../Helpers/interfaces";

// Hooks
import { useLabels } from "../../Hooks/useLabels";

// Components
import { FormAppointmentDetails } from "../../FormAppointmentDetails/FormAppointmentDetails";
import { PracticalInformation } from "../../PracticalInformation/PracticalInformation";
import { CenterInformation } from "../../CenterInformation/CenterInformation";
import { FAQAccordion } from "../../FAQAccordion/FAQAccordion";
import { locationInitialState } from "../../Helpers/initialState/locationInitialState";
import { GtmService } from "../../GtmService";
import { calculateTotalPrice } from "../../Helpers/totalPriceCalculator";
import { useBookingContext } from "../../../BookingButton/BookingContext/BookingContext";
import { OptionalCampaign } from "../../OptionalCampaign/OptionalCampaign";
import { StepLayout } from "../StepLayout/StepLayout";
import { stepHeadings } from "../../stepHeadings";
import { GenericModal } from "../../FormElements/Modal/GenericModal/GenericModal";
import { FourthStepInfoContainer } from "../../FormElements";
import { useTrackEvent } from "@optimizely/react-sdk";

type BookingConfirmedStepProps = {
  state: FormStateType;
  rootData: RootDataType;
};

export const BookingConfirmedStep: FC<BookingConfirmedStepProps> = ({
  state,
  rootData,
}) => {
  const { bookingLabels } = useLabels();
  const fourthStepLabels = bookingLabels?.stepsBookingLabels.fourthStepLabels;
  const {
    waitlist,
    newBookingFilterValues,
    optimizelyExperimentation,
    trackAbEvent,
  } = useBookingContext();
  const doctor = rootData.doctors.filter(
    (d) => d.id == state.appointment.doctorId
  )[0];
  const center = rootData.centers.filter(
    (d) => d.id == state.appointment.centerId
  )[0] || { ...locationInitialState, name: waitlist?.centerName };

  const isWaitlist = !!waitlist?.contentId;

  const wrapperRef = useRef<HTMLDivElement>(null);

  const [showSmsModal, setShowSmsModal] = useState(true);

  useEffect(() => {
    wrapperRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const [track, clientReady, didTimeout] = useTrackEvent();

  useEffect(() => {
    if (
      trackAbEvent &&
      clientReady &&
      optimizelyExperimentation?.events.bookingCompleted
    ) {
      track(optimizelyExperimentation.events.bookingCompleted);
    }
  }, [clientReady]);

  useEffect(() => {
    GtmService.appointmentConfirmed(
      state.specializationName,
      state.service.specializationId,
      getService().bookingName,
      getService().id,
      center?.name,
      center?.id,
      doctor?.name,
      doctor?.id,
      state.appointment.startTime,
      state.bookingResponse.booking?.applyForMembership,
      calculateTotalPrice(state.pricingSummary),
      newBookingFilterValues?.breadcrumbsShown && state.breadcrumbs.length > 0
    );
  }, []);

  const getService = () => {
    return (
      rootData.services.find((s) => s.id === state.appointment.serviceId) ??
      state.service
    );
  };

  const bookingConfirmedStepCustomHeading =
    bookingLabels?.stepsBookingLabels?.newBookingStepsLabels
      .confirmationStepHeading;
  const bookingConfirmedStepHeading =
    bookingConfirmedStepCustomHeading ||
    stepHeadings.bookingConfirmedStepHeading;

  return (
    <div ref={wrapperRef} className="BookingConfirmedStep">
      <StepLayout
        heading={isWaitlist ? "Bekreftelse" : bookingConfirmedStepHeading}
      >
        <FormAppointmentDetails
          doctor={doctor}
          center={center}
          service={getService()}
          specializationName={state.specializationName}
          appointment={state.appointment}
          pricingSummary={state.pricingSummary}
          cancellationMessage={state.cancellationDeadline?.message || ""}
          waitlist={waitlist}
          displayHeading={false}
        />
        <OptionalCampaign
          service={state.service}
          center={center}
          optionalCampaign={fourthStepLabels?.optionalCampaign}
        />
        <PracticalInformation
          specializationName={state.specializationName}
          service={getService()}
          bookingResponse={state.bookingResponse}
          isWaitlist={!!waitlist?.contentId}
        />
        <CenterInformation
          centerName={isWaitlist ? waitlist.centerName : center.name}
          bookingResponse={state.bookingResponse}
        />

        <FourthStepInfoContainer
          noDivider
          className="BookingConfirmedStep__btnLinkWrapper"
        >
          <a href="/" className="BookingConfirmedStep__btnLink">
            {fourthStepLabels?.buttonLabel}
          </a>
        </FourthStepInfoContainer>

        <FAQAccordion localFAQ={state.bookingResponse.faq} />

        {isWaitlist && (
          <GenericModal
            className="BookingConfirmedStep__waitWhileSmsModal"
            onClose={() => setShowSmsModal(false)}
            heading={waitlist?.smsModal.header}
            description={waitlist?.smsModal.description}
            buttonLabel={waitlist?.smsModal.button.link.title}
            isOpen={showSmsModal}
          />
        )}
      </StepLayout>
    </div>
  );
};
